import type { ConfigType } from '@plone/registry';

import AssistantExtras from './components/Assistant/AssistantExtras';
import installSettings from './config/settings';
import { aiEditor } from './config/AiEditorRegistry';
import assistant from './reducers/assistant';

declare const __SERVER__: boolean;

/**
 * The AI editing assistant.
 *
 * Self-contained on purpose: this add-on registers a reducer, a panel and an
 * express proxy, and nothing else. It does not register blocks, styles or a
 * theme, and it holds no dependency on any block add-on — the block set the
 * assistant happens to edit is a separate decision, made by whatever profile
 * the site installs.
 *
 * What connects the two is Volto's registry, not an import: the panel reads
 * `config.blocks.blocksConfig` for the title and icon of whatever block is
 * selected, and the agent is told which block types exist by the backend
 * contract. So the assistant works against any block profile, and a site can
 * adopt a design system without the assistant or the other way round.
 *
 * A customer add-on extends it through `aiEditor(config)` — see
 * `config/AiEditorRegistry` and `docs/extending.md`.
 */
function applyConfig(config: ConfigType) {
  installSettings(config);

  config.addonReducers = {
    ...config.addonReducers,
    assistant,
  };

  config.settings.appExtras = [
    ...(config.settings.appExtras || []),
    { match: '', component: AssistantExtras, props: {} },
  ];

  if (__SERVER__) {
    // The agent proxy and the site reads live on the express server only —
    // required here so the browser bundle stays free of node-only modules.
    // The registry is read at build time, which is after every add-on's
    // applyConfig has run, so a customer's handlers are already in it.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const {
      default: aiAgentMiddleware,
      defaultHandlers,
    } = require('./server/middleware');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { PloneClient } = require('./server/PloneClient');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { ContentMapper } = require('./server/ContentMapper');

    const plone = new PloneClient();
    const deps = { plone, mapper: new ContentMapper(plone) };

    config.settings.expressMiddleware = [
      ...((config.settings.expressMiddleware || []) as unknown[]),
      aiAgentMiddleware({
        deps,
        registry: aiEditor(config).applyTo(defaultHandlers(deps), deps),
      }),
    ];
  }

  return config;
}

export default applyConfig;

// The public surface a customer add-on builds on.
export { aiEditor, AiEditorRegistry } from './config/AiEditorRegistry';
export {
  AgentClient,
  canonicalPagePathname,
  AGENT_VERSION,
} from './api/AgentClient';
export { HttpAgentClient } from './api/HttpAgentClient';
export { MockAgentClient } from './api/MockAgentClient';
export { HttpTransport, HttpError } from './api/HttpTransport';
export { AgentTurn } from './api/AgentTurn';
export { AttachmentUploader } from './api/AttachmentUploader';
export { agentClient } from './api/client';
// The server-side classes are deliberately not re-exported here: this module
// is the browser bundle's entry point. Import them from their own paths —
// `@interaktiv/volto-ai-editor/server/SiteReadHandler` and friends — which is
// what `docs/extending.md` shows and what keeps unpdf out of the browser.
export type * from './types';
